import { parseRateRange, parseHoursRange, hoursLabel } from "../../supabase/functions/_shared/pay-range";
// Structured Data generators for SEO
// Note: Schema.org requires specific English values for certain fields (dayOfWeek, employmentType, etc.)
// But descriptions and user-facing content should be in Dutch

export const ORG_ID = "https://perfectezorgmatch.nl/#organization";
/** Reference to the single site Organization (defined once per page in index.html). */
export const ORG_REF = { "@id": ORG_ID } as const;

/**
 * The one Organization entity for Perfecte Zorgmatch. Must stay identical to the
 * JSON-LD in index.html. No logo until a real square logo (>=112px PNG/SVG) exists;
 * sameAs only lists social profiles that are actually linked on the site (none yet).
 */
export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: "Perfecte Zorgmatch",
  alternateName: "De Perfecte Zorgmatch",
  legalName: "Deer Peter B.V.",
  url: "https://perfectezorgmatch.nl",
  slogan: "Zorg in de buurt",
  email: "perfectezorgmatch@deerpeter.nl",
  parentOrganization: { "@type": "Organization", name: "Deer Peter B.V.", url: "https://deerpeter.nl" },
  contactPoint: {
    "@type": "ContactPoint",
    email: "perfectezorgmatch@deerpeter.nl",
    contactType: "customer service",
    areaServed: ["NL", "BE"],
    availableLanguage: "nl",
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: "Stationsplein 3",
    addressLocality: "Zwolle",
    postalCode: "8011 CW",
    addressCountry: "NL",
  },
});

export const generateWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Perfecte Zorgmatch",
  url: "https://perfectezorgmatch.nl",
  inLanguage: "nl-NL",
  publisher: ORG_REF,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: "https://perfectezorgmatch.nl/zoek-zorgverlener?q={search_term_string}",
    },
    "query-input": "required name=search_term_string",
  },
});

export const generateCaregiverProfileSchema = (profile: {
  firstName: string;
  lastName: string;
  city?: string;
  profileText?: string;
  profilePhotoUrl?: string;
  slug: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name: `${profile.firstName} ${profile.lastName}`,
  jobTitle: "Zorgverlener",
  description: profile.profileText || `${profile.firstName} is een zorgverlener bij Perfecte Zorgmatch`,
  image: profile.profilePhotoUrl,
  url: `https://perfectezorgmatch.nl/zorgverlener/${profile.slug}`,
  address: profile.city
    ? {
        "@type": "PostalAddress",
        addressLocality: profile.city,
        addressCountry: "NL",
      }
    : undefined,
});

/** Parses a plain numeric hourly rate ("25", "25,50", "€ 25.50"); anything else → null. */
export const parseHourlyRate = (raw: unknown): number | null => {
  const r = parseRateRange(raw);
  return r && r.min === r.max ? r.min : null;
};

/**
 * JobPosting for an open hulpvraag. Returns null for closed postings.
 * No validThrough and no logo. description is the public summary that is also
 * visible on the page. Only public metadata: never client name, diagnoses or age;
 * postcode only as its 4 digits, which the page shows too.
 */
export const generateJobPostingSchema = (posting: {
  title: string;
  description?: string;
  city?: string;
  createdAt: string;
  updatedAt?: string | null;
  closingDate?: string | null;
  status?: string | null;
  hourlyRate?: string | number | null;
  careType?: string[];
  postcode4?: string | null;
  province?: string | null;
  hoursPerWeek?: number | string | null;
  jobNumber?: string | null;
}) => {
  if (posting.status && posting.status !== "active") return null;
  const rate = parseRateRange(posting.hourlyRate);
  const hr = parseHoursRange(posting.hoursPerWeek);
  const h = hr ? hr.max : null;
  const employmentType = h === null ? ["CONTRACTOR", "PART_TIME"] : ["CONTRACTOR", h >= 36 ? "FULL_TIME" : "PART_TIME"];
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: posting.title,
    description: posting.description || posting.title,
    datePosted: posting.createdAt,
    employmentType,
    directApply: false,
    ...(posting.jobNumber && {
      identifier: { "@type": "PropertyValue", name: "Perfecte Zorgmatch", value: posting.jobNumber },
    }),
    ...(h !== null && { workHours: hoursLabel(posting.hoursPerWeek) ?? undefined }),
    hiringOrganization: { "@id": ORG_ID, "@type": "Organization", name: "Perfecte Zorgmatch", sameAs: "https://perfectezorgmatch.nl" },
    ...(posting.city && {
      jobLocation: {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          addressLocality: posting.city,
          ...(posting.postcode4 && /^\d{4}$/.test(posting.postcode4) && { postalCode: posting.postcode4 }),
          ...(posting.province && { addressRegion: posting.province }),
          addressCountry: "NL",
        },
      },
    }),
    ...(rate !== null && {
      baseSalary: {
        "@type": "MonetaryAmount",
        currency: "EUR",
        value: rate.min === rate.max
          ? { "@type": "QuantitativeValue", value: rate.min, unitText: "HOUR" }
          : { "@type": "QuantitativeValue", minValue: rate.min, maxValue: rate.max, unitText: "HOUR" },
      },
    }),
    industry: "Gezondheidszorg",
    occupationalCategory: "31-1120.00",
  };
};

export const generateFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

export const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url.startsWith("http") ? item.url : `https://perfectezorgmatch.nl${item.url}`,
  })),
});

// Service schema for AI-friendly structured data
export const generateServiceSchema = (service: {
  name: string;
  description: string;
  serviceType?: string;
  areaServed?: string;
  provider?: string;
  url?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: service.name,
  description: service.description,
  serviceType: service.serviceType || "Zorgbemiddeling",
  provider: service.provider && service.provider !== "Perfecte Zorgmatch"
    ? { "@type": "Organization", name: service.provider }
    : ORG_REF,
  areaServed: {
    "@type": "Country",
    name: service.areaServed || "Nederland",
  },
  url: service.url,
});

// HowTo schema for step-by-step guides (AI-friendly)
export const generateHowToSchema = (howTo: {
  name: string;
  description: string;
  steps: { name: string; text: string }[];
}) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: howTo.name,
  description: howTo.description,
  step: howTo.steps.map((step, index) => ({
    "@type": "HowToStep",
    position: index + 1,
    name: step.name,
    text: step.text,
  })),
});

// Article schema for content pages
export const generateArticleSchema = (article: {
  headline: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
  url: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: article.headline,
  description: article.description,
  datePublished: article.datePublished,
  dateModified: article.dateModified || article.datePublished,
  author: article.author && article.author !== "Perfecte Zorgmatch"
    ? { "@type": "Organization", name: article.author }
    : ORG_REF,
  publisher: ORG_REF,
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": article.url,
  },
});

// Review schema for caregiver reviews - Google compliant (uses Service, not Person)
// Google rejects reviews about Person types, so we model reviews as being about a Service
export const generateReviewSchema = (review: {
  reviewBody: string;
  authorName: string;
  caregiverName: string;
  position?: number;
}) => ({
  "@type": "Review",
  ...(review.position !== undefined && { position: review.position }),
  reviewBody: review.reviewBody,
  author: {
    "@type": "Person",
    name: review.authorName,
  },
  itemReviewed: {
    "@type": "Service",
    name: `Persoonlijke begeleiding door zorgverlener ${review.caregiverName}`,
    provider: {
      "@type": "Organization",
      name: "Perfecte Zorgmatch",
      url: "https://perfectezorgmatch.nl",
    },
  },
});

// Reviews page structured data - Google compliant
export const generateReviewsPageSchema = (reviews: {
  caregiverName: string;
  reviewBody: string;
  authorName: string;
}[]) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Aanbevelingen van Zorgverleners",
  description: "Lees echte ervaringen en aanbevelingen van cliënten over zorgverleners aangesloten bij Perfecte Zorgmatch.",
  url: "https://perfectezorgmatch.nl/aanbevelingen",
  mainEntity: {
    "@type": "ItemList",
    itemListElement: reviews.map((review, index) => 
      generateReviewSchema({
        ...review,
        position: index + 1,
      })
    ),
  },
});
